import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const SiteContext = createContext(null);

const DEFAULT_THEME = "dark-premium";

function applyTheme(themeKey, customTokens) {
  const t = themeKey || DEFAULT_THEME;
  document.documentElement.setAttribute("data-theme", t);

  // Apply custom token overrides through a single injected <style> block that is
  // SCOPED to the currently-active theme, e.g. `:root[data-theme="dark-premium"]{…}`.
  // This is non-destructive and preset-switch-safe:
  //   - It never writes inline styles on documentElement (which would outrank
  //     BOTH preset blocks permanently and could brick the admin chrome).
  //   - Switching presets rewrites the block for the new active theme, so the
  //     other preset's default tokens are restored automatically.
  //   - Clearing all tokens empties the block, restoring the pristine preset.
  // `:root[data-theme="x"]` (specificity 0,2,0) beats the bare `[data-theme="x"]`
  // preset block (0,1,0), so overrides win without any !important.
  let styleEl = document.getElementById("ct-custom-tokens");
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = "ct-custom-tokens";
    document.head.appendChild(styleEl);
  }
  const decls = [];
  if (customTokens && typeof customTokens === "object") {
    for (const [k, v] of Object.entries(customTokens)) {
      if (!k || !v) continue;
      decls.push(`${k}: ${v};`);
    }
  }
  styleEl.textContent = decls.length
    ? `:root[data-theme="${t}"]{${decls.join("")}}`
    : "";
}

export function SiteProvider({ children }) {
  const [site, setSite] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const [s, sec] = await Promise.all([
        api.get("/public/site"),
        api.get("/public/sections"),
      ]);
      setSite(s.data);
      setSections(sec.data);
      const active = s.data?.theme?.active || DEFAULT_THEME;
      const tokens = s.data?.theme?.custom_tokens || {};
      applyTheme(active, tokens);
    } catch (e) {
      console.error("Failed to load site config", e);
      applyTheme(DEFAULT_THEME, {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const sectionVisible = (key) => sections.some((s) => s.key === key && s.visible);
  const sectionContent = (key) => sections.find((s) => s.key === key)?.content || null;

  return (
    <SiteContext.Provider value={{ site, sections, loading, reload, sectionVisible, sectionContent }}>
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error("useSite must be used inside SiteProvider");
  return ctx;
}
